import { Dispatch, useCallback, useEffect, useState } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { Section } from 'UIPackage/FormModule/components/Section';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IKnmErknmFormJudgment } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormJudgment';
import { IKnmErknmFormJudgmentActsAndDecisions } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormJudgmentActsAndDecisions';
import { IKnmErknmFormJudgmentAgainstInfo } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormJudgmentAgainstInfo';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import {
  getJudgmentAgainstOfficialPersonBlockId,
  getJudgmentCancellationInfoBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';

import { catalogGet } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { JudgmentActionsEnum } from '../../../CorePackage/CatalogsModule/enums/JudgmentActionsEnum';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { CheckboxField } from '../../../UIPackage/MainModule/components/CheckboxField';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { AddJudgmentModal } from './AddJudgmentModal';
import { DecisionJudgmentDecisionInfo } from './DecisionJudgmentDecisionInfo';
import { DecisionJudgmentAgainstInfo } from './DecisionJudgmentAgainstInfo';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IDecisionJudgmentItemSubjectDataItemProps {
  watchOnly: boolean;
  judgmentItemValue: IKnmErknmFormJudgment;
  judgmentItemName: string;
  judgmentItemSubjectDataItemName: string;
  judgmentItemSubjectDataItemId: string;
  cancellationActsAndDecisions: IKnmErknmFormJudgmentActsAndDecisions[] | undefined;
  isJudgmentAgainstOfficialPerson: boolean | undefined;
  isJudgmentAgainstOrganizations: boolean | undefined;
  judgmentAgainstOrganizations: IKnmErknmFormJudgmentAgainstInfo | undefined;
  formRef: IDecisionFormRef | undefined;
}

export const DecisionJudgmentItemSubjectDataItem = ({
  watchOnly,
  formRef,
  judgmentItemValue,
  judgmentItemName,
  cancellationActsAndDecisions,
  judgmentItemSubjectDataItemName,
  judgmentItemSubjectDataItemId,
  isJudgmentAgainstOfficialPerson,
  isJudgmentAgainstOrganizations,
  judgmentAgainstOrganizations,
}: IDecisionJudgmentItemSubjectDataItemProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const openModal = () => {
    setIsModalVisible(true);
  };

  const saveModal = () => {
    setIsModalVisible(false);
  };

  const cancelModal = useCallback(() => {
    formRef?.current.change(
      `${judgmentItemSubjectDataItemName}.cancellationActsAndDecisions` as keyof IDecisionForm,
      // @ts-ignore
      cancellationActsAndDecisions,
    );
    setIsModalVisible(false);
  }, [formRef, judgmentItemSubjectDataItemName, cancellationActsAndDecisions]);

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.JUDGMENT_CANCELLATION_INFORMATION));
    dispatch(catalogGet(CommonCatalogsNamesEnum.JUDGMENT_PUNISHMENT_INDEX));
  }, [dispatch]);

  const addJudgmentAgainstOfficialPerson = useCallback(() => {
    if (!watchOnly && push) {
      const newJudgmentAgainstOfficialPerson: IKnmErknmFormJudgmentAgainstInfo = {
        guid: undefined,
        officialPersonFio: undefined,
        officialPersonJobTitle: undefined,
        prosecutionType: undefined,
        punishmentType: undefined,
        amountOfPunishment: undefined,
        punishmentIndex: undefined,
        punishmentNoteLawsuits: undefined,
        cancellationInformation: undefined,
        explanation: undefined,
        isNew: true,
        listId: uuidv4(),
      };

      push(`${judgmentItemSubjectDataItemName}.judgmentAgainstOfficialPerson`, newJudgmentAgainstOfficialPerson);
    }
  }, [push, watchOnly, judgmentItemSubjectDataItemName]);
  const makeRemoveJudgmentAgainstOfficialPersonHandler = (index: number) => () =>
    !watchOnly && remove && remove(`${judgmentItemSubjectDataItemName}.judgmentAgainstOfficialPerson`, index);

  useEffect(() => {
    if (!isJudgmentAgainstOrganizations) {
      formRef?.current.change(`${judgmentItemSubjectDataItemName}.judgmentAgainstOrganizations` as keyof IDecisionForm);
    }
  }, [isJudgmentAgainstOrganizations]);

  useEffect(() => {
    if (!isJudgmentAgainstOfficialPerson) {
      formRef?.current.change(
        `${judgmentItemSubjectDataItemName}.judgmentAgainstOfficialPerson` as keyof IDecisionForm,
      );
    }
  }, [isJudgmentAgainstOfficialPerson]);

  const renderJudgmentAgainstOfficialPersonItem = () => {
    return (
      <FieldArray name={`${judgmentItemSubjectDataItemName}.judgmentAgainstOfficialPerson`}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormJudgmentAgainstInfo, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((judgmentAgainstName, judgmentAgainstOfficialPersonNameIndex) => {
            const judgmentAgainstValue: IKnmErknmFormJudgmentAgainstInfo =
              fields.value?.[judgmentAgainstOfficialPersonNameIndex];
            const judgmentAgainstOfficialPersonId = getJudgmentAgainstOfficialPersonBlockId(
              judgmentItemSubjectDataItemId,
              judgmentAgainstValue.listId,
            );

            const hasError = checkHasErrorInCollapseBlock(formRef, judgmentAgainstName);

            return (
              <KnmCollapse
                id={judgmentAgainstOfficialPersonId}
                theme="fields"
                title={'Должностное лицо'}
                isNew={judgmentAgainstValue.isNew}
                isNewTitle={'Новое'}
                opened={true}
                onClose={
                  !watchOnly
                    ? makeRemoveJudgmentAgainstOfficialPersonHandler(judgmentAgainstOfficialPersonNameIndex)
                    : undefined
                }
                key={judgmentAgainstName}
                changeOpenedCallback={e =>
                  setKnmCollapseOpened(state => ({
                    ...state,
                    [judgmentAgainstName]: e,
                  }))
                }
                error={hasError && knmCollapseOpened[judgmentAgainstName]}
              >
                <>
                  {isJudgmentAgainstOfficialPerson && (
                    <Row>
                      <FieldBlock id={`${judgmentAgainstOfficialPersonId}.officialPersonFio`} size="half">
                        <TextInputField
                          name={`${judgmentAgainstName}.officialPersonFio`}
                          disabled={watchOnly}
                          placeholder="Ф.И.О"
                        />
                      </FieldBlock>
                      <FieldBlock id={`${judgmentAgainstOfficialPersonId}.officialPersonJobTitle`} size="half">
                        <TextInputField
                          name={`${judgmentAgainstName}.officialPersonJobTitle`}
                          disabled={watchOnly}
                          placeholder="Должность"
                        />
                      </FieldBlock>
                    </Row>
                  )}
                  <DecisionJudgmentAgainstInfo
                    formRef={formRef}
                    judgmentItemAgainstInfoName={judgmentAgainstName}
                    judgmentItemAgainstValue={judgmentAgainstValue}
                    judgmentItemSubjectDataItemId={judgmentAgainstOfficialPersonId}
                    watchOnly={watchOnly}
                  />
                </>
              </KnmCollapse>
            );
          });
        }}
      </FieldArray>
    );
  };

  return (
    <>
      {judgmentItemValue.judgmentAction === JudgmentActionsEnum.ADD_JUDGMENT && (
        <>
          <Row>
            <FieldBlock id={`${judgmentItemSubjectDataItemId}.isJudgmentAgainstOrganizations`}>
              <CheckboxField
                name={`${judgmentItemSubjectDataItemName}.isJudgmentAgainstOrganizations`}
                checked={isJudgmentAgainstOrganizations}
                label="Судебный акт принят в отношении контролируемого лица"
                type="checkbox"
                theme="system"
                disabled={watchOnly}
              />
            </FieldBlock>
          </Row>
          {isJudgmentAgainstOrganizations && (
            <DecisionJudgmentAgainstInfo
              formRef={formRef}
              judgmentItemAgainstInfoName={`${judgmentItemSubjectDataItemName}.judgmentAgainstOrganizations`}
              judgmentItemAgainstValue={judgmentAgainstOrganizations}
              watchOnly={watchOnly}
              judgmentItemSubjectDataItemId={judgmentItemSubjectDataItemId}
            />
          )}
          <Row notSpace notWrap>
            <FieldBlock id={`${judgmentItemSubjectDataItemId}.isJudgmentAgainstOfficialPerson`} size="content">
              <CheckboxField
                name={`${judgmentItemSubjectDataItemName}.isJudgmentAgainstOfficialPerson`}
                checked={isJudgmentAgainstOfficialPerson}
                label="Судебный акт принят в отношении должностного лица "
                type="checkbox"
                theme="system"
                disabled={watchOnly}
              />
            </FieldBlock>
            {isJudgmentAgainstOfficialPerson && (
              <FieldBlock size="content">
                <Button
                  className="shared-btn-add"
                  text="Добавить"
                  type="button"
                  variant="link"
                  color="primary"
                  disabled={watchOnly}
                  onClick={addJudgmentAgainstOfficialPerson}
                />
              </FieldBlock>
            )}
          </Row>
          {isJudgmentAgainstOfficialPerson && renderJudgmentAgainstOfficialPersonItem()}
        </>
      )}
      {(judgmentItemValue.judgmentAction === JudgmentActionsEnum.ANNULMENT_AKT ||
        judgmentItemValue.judgmentAction === JudgmentActionsEnum.ANNULMENT_JUDGMENT) && (
        <>
          <AddJudgmentModal
            formRef={formRef}
            visible={isModalVisible}
            modalType="decision"
            judgmentItemName={judgmentItemName}
            judgmentItemSubjectDataItemName={judgmentItemSubjectDataItemName}
            onSave={saveModal}
            onCancel={cancelModal}
            organization={formRef?.current.getFieldState('organization')?.value}
          />
          <Section
            title="Сведения о решениях в рамках решения, которые отменены или изменены"
            type="list"
            id={getJudgmentCancellationInfoBlockId(judgmentItemSubjectDataItemId)}
            customButton={
              <Button
                className="shared-btn-add"
                text="Добавить"
                type="button"
                variant="link"
                color="primary"
                disabled={watchOnly}
                onClick={openModal}
              />
            }
          >
            {!cancellationActsAndDecisions?.length && <p className="shared-input-error">Поле не может быть пустым</p>}
            {cancellationActsAndDecisions && (
              <DecisionJudgmentDecisionInfo
                watchOnly={watchOnly}
                judgmentItemName={judgmentItemName}
                judgmentItemSubjectDataItemName={judgmentItemSubjectDataItemName}
                judgmentItemSubjectDataItemId={judgmentItemSubjectDataItemId}
                formRef={formRef}
              />
            )}
          </Section>
        </>
      )}
    </>
  );
};
